import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../homepage/navbar/Navbar';
import { useThemeContext } from '../../context/theme-context';
import { FiArrowLeft, FiEdit, FiEye, FiSave, FiBold, FiItalic, FiLink, FiList, FiCode, FiImage, FiAlignLeft, FiAlignCenter, FiAlignRight, FiAlignJustify, FiCornerDownLeft, FiUpload, FiRotateCcw, FiRotateCw } from 'react-icons/fi';
import './admin.css';
import '../blog/blog.css'; // import blog details styling for live preview

const CATEGORIES = ['Technology', 'Career Guidance', 'Data Science', 'Data Analytics', 'Digital Marketing'];

const AdminBlogEdit = () => {
  const { themeState } = useThemeContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  // Form State
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [tags, setTags] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [readTime, setReadTime] = useState('');
  const [authorName, setAuthorName] = useState('SkillBridge Team');
  const [published, setPublished] = useState(true);

  // UX State
  const [activeTab, setActiveTab] = useState('edit'); // 'edit' or 'preview'
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/')) {
      return `${API_URL}${url}`;
    }
    return url;
  };

  useEffect(() => {
    if (isEditMode) {
      fetchBlogDetails();
    }
  }, [id]);

  const fetchBlogDetails = async () => {
    try {
      setFetching(true);
      setError(null);
      const res = await fetch(`${API_URL}/api/blogs/${id}`);
      const json = await res.json();
      
      if (res.ok && json.data) {
         const blog = json.data;
         setTitle(blog.title || '');
         setSlug(blog.slug || '');
         setSubtitle(blog.subtitle || '');
         setContent(blog.content || '');
         setCategory(blog.category || CATEGORIES[0]);
         setTags(Array.isArray(blog.tags) ? blog.tags.join(', ') : '');
         setImageUrl(blog.imageUrl || '');
         setReadTime(blog.readTime || '');
         setAuthorName(blog.authorName || 'SkillBridge Team');
         setPublished(blog.published !== undefined ? blog.published : true);
      } else {
        setError(json.message || 'Failed to load blog details');
      }
    } catch (err) {
      setError('Connection to backend failed.');
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    setError(null);
    setSuccess(null);
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/blogs/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: formData,
        credentials: 'include'
      });
      const json = await res.json();
      if (res.ok && json.url) {
        setImageUrl(json.url);
        setSuccess('Image uploaded successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(json.message || 'Image upload failed.');
      }
    } catch (err) {
      setError('Failed to upload image to server.');
      console.error(err);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleToolbarImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    setError(null);
    setSuccess(null);
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/blogs/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: formData,
        credentials: 'include'
      });
      const json = await res.json();
      if (res.ok && json.url) {
        const textarea = document.getElementById('blog-content-textarea');
        if (textarea) {
          textarea.focus();
          const replacement = `![${file.name.split('.')[0] || 'uploaded image'}](${json.url})`;
          try {
            document.execCommand('insertText', false, replacement);
          } catch (err) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = textarea.value;
            const newValue = text.substring(0, start) + replacement + text.substring(end);
            setContent(newValue);
            setTimeout(() => {
              textarea.focus();
              textarea.setSelectionRange(start + replacement.length, start + replacement.length);
            }, 0);
          }
        }
        setSuccess('Image uploaded and inserted successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(json.message || 'Image upload failed.');
      }
    } catch (err) {
      setError('Failed to upload image to server.');
      console.error(err);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const insertMarkdown = (syntax) => {
    const textarea = document.getElementById('blog-content-textarea');
    if (!textarea) return;

    textarea.focus();
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end);
    
    let replacement = '';
    switch(syntax) {
      case 'bold':
        replacement = `**${selectedText || 'bold text'}**`;
        break;
      case 'italic':
        replacement = `*${selectedText || 'italic text'}*`;
        break;
      case 'h1':
        replacement = `# ${selectedText || 'Heading 1'}`;
        break;
      case 'h2':
        replacement = `## ${selectedText || 'Heading 2'}`;
        break;
      case 'h3':
        replacement = `### ${selectedText || 'Heading 3'}`;
        break;
      case 'link':
        replacement = `[${selectedText || 'link text'}](https://example.com)`;
        break;
      case 'list':
        replacement = `\n- ${selectedText || 'list item'}`;
        break;
      case 'code':
        replacement = `\n\`\`\`javascript\n${selectedText || '// code here'}\n\`\`\``;
        break;
      case 'quote':
        replacement = `\n> ${selectedText || 'blockquote'}`;
        break;
      case 'image':
        replacement = `![${selectedText || 'image description'}](https://example.com/image.jpg)`;
        break;
      case 'newline':
        replacement = `  \n`; // double-space + newline is correct markdown line break
        break;
      case 'align-left':
        replacement = `<div style="text-align: left">${selectedText || 'Left aligned text'}</div>`;
        break;
      case 'align-center':
        replacement = `<div style="text-align: center">${selectedText || 'Centered text'}</div>`;
        break;
      case 'align-right':
        replacement = `<div style="text-align: right">${selectedText || 'Right aligned text'}</div>`;
        break;
      case 'align-justify':
        replacement = `<div style="text-align: justify">${selectedText || 'Justified text'}</div>`;
        break;
      default:
        return;
    }
    
    try {
      document.execCommand('insertText', false, replacement);
    } catch (err) {
      const newValue = text.substring(0, start) + replacement + text.substring(end);
      setContent(newValue);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + replacement.length, start + replacement.length);
      }, 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!title.trim() || !content.trim() || !category.trim()) {
      setError('Title, content, and category are required.');
      return;
    }

    // Rough read time calculation if not specified
    let finalReadTime = readTime.trim();
    if (!finalReadTime) {
      const wordsCount = content.trim().split(/\s+/).length;
      const calculatedMin = Math.max(1, Math.round(wordsCount / 200));
      finalReadTime = `${calculatedMin} min read`;
    }

    const blogData = {
      title,
      slug,
      subtitle,
      content,
      category,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      imageUrl,
      readTime: finalReadTime,
      authorName,
      published
    };

    try {
      setLoading(true);
      const url = isEditMode ? `${API_URL}/api/blogs/${id}` : `${API_URL}/api/blogs`;
      const method = isEditMode ? 'PUT' : 'POST';
      const token = localStorage.getItem('token');
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(blogData),
        credentials: 'include'
      });

      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      const json = await res.json();
      if (res.ok) {
        setSuccess(isEditMode ? 'Blog updated successfully!' : 'Blog created successfully!');
        setTimeout(() => {
          navigate('/admin/blogs');
        }, 1500);
      } else {
        setError(json.message || 'Failed to save blog post.');
      }
    } catch (err) {
      setError('Failed to connect to the server.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const parseInlineMarkdown = (text) => {
    if (!text) return '';
    let processed = text;
    // Bold: **text** -> <strong>text</strong>, __text__ -> <strong>text</strong>
    processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    processed = processed.replace(/__(.*?)__/g, '<strong>$1</strong>');
    // Italic: *text* -> <em>text</em>, _text_ -> <em>text</em>
    processed = processed.replace(/\*(.*?)\*/g, '<em>$1</em>');
    processed = processed.replace(/_(.*?)_/g, '<em>$1</em>');
    // Inline code: `code` -> <code>code</code>
    processed = processed.replace(/`(.*?)`/g, '<code class="blog__content-code-inline" style="background: rgba(0,0,0,0.05); padding: 0.2rem 0.4rem; border-radius: 4px; font-family: monospace;">$1</code>');
    // Images: ![alt](url) -> img tag
    processed = processed.replace(/!\[(.*?)\]\((.*?)\)/g, (match, alt, url) => {
      const fullUrl = url.startsWith('/api/') ? `${API_URL}${url}` : url;
      return `<img src="${fullUrl}" alt="${alt}" class="blog__content-image" style="max-width: 100%; border-radius: 8px; margin: 1rem 0; display: block;" />`;
    });
    // Links: [title](url) -> anchor tag
    processed = processed.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="blog__content-link">$1</a>');
    return processed;
  };

  // Helper to split paragraph/headers with markdown elements
  const renderPreviewContent = (content) => {
    if (!content) return <p className="blog__detail-paragraph">Start typing content in the editor...</p>;
    
    const lines = content.split('\n');
    const renderedElements = [];
    let currentList = [];
    let listType = null; // 'bullet' or 'ordered'
    let inCodeBlock = false;
    let codeContent = [];
    
    const flushList = (key) => {
      if (currentList.length > 0) {
        if (listType === 'bullet') {
          renderedElements.push(
            <ul key={`list-${key}`} className="blog__detail-list">
              {currentList.map((item, idx) => (
                <li key={idx} className="blog__detail-list-item" dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(item) }} />
              ))}
            </ul>
          );
        } else if (listType === 'ordered') {
          renderedElements.push(
            <ol key={`list-${key}`} className="blog__detail-list-ordered" style={{ paddingLeft: '1.5rem', marginBottom: '1.5rem' }}>
              {currentList.map((item, idx) => (
                <li key={idx} className="blog__detail-list-item" style={{ marginBottom: '0.4rem' }} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(item) }} />
              ))}
            </ol>
          );
        }
        currentList = [];
        listType = null;
      }
    };

    lines.forEach((line, i) => {
      const trimmed = line.trim();
      
      // Code Blocks
      if (trimmed.startsWith('```')) {
        if (inCodeBlock) {
          renderedElements.push(
            <pre key={`code-${i}`} className="blog__detail-code-block" style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '8px', overflowX: 'auto', fontFamily: 'monospace', margin: '1.5rem 0', border: '1px solid rgba(0,0,0,0.05)' }}>
              <code>{codeContent.join('\n')}</code>
            </pre>
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          flushList(i);
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        return;
      }

      // Horizontal Rules (3 or more hyphens, asterisks, or underscores)
      if (/^(?:-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
        flushList(i);
        renderedElements.push(
          <hr key={i} className="blog__detail-hr" style={{ border: '0', height: '1px', background: '#e0e0e0', margin: '2rem 0', display: 'block' }} />
        );
        return;
      }

      if (!trimmed) {
        flushList(i);
        renderedElements.push(
          <div key={`br-${i}`} style={{ height: '1.2rem' }}></div>
        );
        return;
      }

      // Inline alignment wrappers (check if line starts with HTML alignment tag)
      if (trimmed.startsWith('<div style="text-align:') && trimmed.endsWith('</div>')) {
        flushList(i);
        renderedElements.push(
          <div key={i} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(trimmed) }} />
        );
        return;
      }

      // Blockquotes
      if (trimmed.startsWith('> ')) {
        flushList(i);
        renderedElements.push(
          <blockquote key={i} className="blog__detail-blockquote" style={{ borderLeft: '4px solid var(--color-primary)', paddingLeft: '1rem', fontStyle: 'italic', margin: '1.5rem 0', color: '#555' }} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(trimmed.replace(/^>\s*/, '')) }} />
        );
        return;
      }

      // Bullet List Items
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        if (listType !== 'bullet') {
          flushList(i);
          listType = 'bullet';
        }
        const itemText = trimmed.replace(/^[-*]\s+/, '');
        currentList.push(itemText);
        return;
      }

      // Ordered List Items
      if (/^\d+\.\s+/.test(trimmed)) {
        if (listType !== 'ordered') {
          flushList(i);
          listType = 'ordered';
        }
        const itemText = trimmed.replace(/^\d+\.\s+/, '');
        currentList.push(itemText);
        return;
      }

      // Flush list if any other element is encountered
      flushList(i);

      // Headings
      if (trimmed.startsWith('###')) {
        renderedElements.push(
          <h4 key={i} className="blog__detail-subheading-small" style={{ fontSize: '1.25rem', marginTop: '1.5rem', fontWeight: 600 }} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(trimmed.replace(/^###\s*/, '')) }} />
        );
      } else if (trimmed.startsWith('##')) {
        renderedElements.push(
          <h3 key={i} className="blog__detail-subheading" style={{ fontSize: '1.5rem', marginTop: '1.8rem', fontWeight: 600 }} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(trimmed.replace(/^##\s*/, '')) }} />
        );
      } else if (trimmed.startsWith('#')) {
        renderedElements.push(
          <h2 key={i} className="blog__detail-subheading-large" style={{ fontSize: '1.8rem', marginTop: '2rem', fontWeight: 700 }} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(trimmed.replace(/^#\s*/, '')) }} />
        );
      } else {
        // Paragraph
        renderedElements.push(
          <p key={i} className="blog__detail-paragraph" dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(line) }} />
        );
      }
    });

    // Flush any remaining list at the end
    flushList(lines.length);

    return renderedElements;
  };

  return (
    <main className={`${themeState.primary} ${themeState.background} admin__page`}>
      <Navbar />

      <section className="admin__container" style={{ maxWidth: '1000px' }}>
        <br/>
        <div className="admin__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button className="btn sm" onClick={() => navigate('/admin/blogs')}>← Cancel</button>
            <h2>{isEditMode ? 'Edit Blog Post' : 'Create Blog Post'}</h2>
          </div>
          
          <div className="editor__tab-buttons" style={{ display: 'flex', gap: '0.2rem', background: 'var(--color-light)', padding: '0.25rem', borderRadius: '8px' }}>
            <button 
              type="button"
              className={`btn sm ${activeTab === 'edit' ? 'primary' : ''}`}
              onClick={() => setActiveTab('edit')}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', border: 'none' }}
            >
              <FiEdit /> Editor
            </button>
            <button 
              type="button"
              className={`btn sm ${activeTab === 'preview' ? 'primary' : ''}`}
              onClick={() => setActiveTab('preview')}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', border: 'none' }}
            >
              <FiEye /> Preview
            </button>
          </div>
        </div>

        {error && <div className="admin__error">{error}</div>}
        {success && <div className="admin__error" style={{ background: 'rgba(76, 175, 80, 0.15)', color: '#2e7d32' }}>{success}</div>}
        {fetching && <div className="admin__loading">Loading details...</div>}

        {!fetching && activeTab === 'edit' && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', marginTop: '1rem' }}>
            <div className="admin__form-grid">
              
              {/* Title */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <label style={{ fontWeight: 600 }}>Title *</label>
                <input 
                  type="text" 
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)} 
                  placeholder="e.g., Guide to Web Development Internships"
                  required
                  style={{ padding: '0.7rem', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)', background: 'var(--color-white)' }}
                />
              </div>

              {/* Slug */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <label style={{ fontWeight: 600 }}>Custom Slug (URL path - optional)</label>
                <input 
                  type="text" 
                  value={slug} 
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '-'))} 
                  placeholder="e.g., web-development-guide (auto-generated if blank)"
                  style={{ padding: '0.7rem', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)', background: 'var(--color-white)' }}
                />
              </div>

              {/* Subtitle */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <label style={{ fontWeight: 600 }}>Subtitle / Excerpt</label>
                <input 
                  type="text" 
                  value={subtitle} 
                  onChange={(e) => setSubtitle(e.target.value)} 
                  placeholder="e.g., A comprehensive guide to kickstarting your engineering journey"
                  style={{ padding: '0.7rem', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)', background: 'var(--color-white)' }}
                />
              </div>

              {/* Category */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <label style={{ fontWeight: 600 }}>Category *</label>
                <select 
                  value={category} 
                  onChange={(e) => setCategory(e.target.value)}
                  style={{ padding: '0.7rem', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)', background: 'var(--color-white)', fontFamily: 'inherit' }}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <label style={{ fontWeight: 600 }}>Tags (comma-separated)</label>
                <input 
                  type="text" 
                  value={tags} 
                  onChange={(e) => setTags(e.target.value)} 
                  placeholder="e.g., react, javascript, coding"
                  style={{ padding: '0.7rem', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)', background: 'var(--color-white)' }}
                />
              </div>

              {/* Read Time */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <label style={{ fontWeight: 600 }}>Read Time (Optional)</label>
                <input 
                  type="text" 
                  value={readTime} 
                  onChange={(e) => setReadTime(e.target.value)} 
                  placeholder="e.g., 5 min read (or calculated automatically)"
                  style={{ padding: '0.7rem', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)', background: 'var(--color-white)' }}
                />
              </div>

              {/* Author Name */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <label style={{ fontWeight: 600 }}>Author Name</label>
                <input 
                  type="text" 
                  value={authorName} 
                  onChange={(e) => setAuthorName(e.target.value)} 
                  placeholder="SkillBridge Team"
                  style={{ padding: '0.7rem', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)', background: 'var(--color-white)' }}
                />
              </div>

              {/* Published checkbox */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '1.8rem' }}>
                <input 
                  type="checkbox" 
                  id="published"
                  checked={published} 
                  onChange={(e) => setPublished(e.target.checked)} 
                  style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer' }}
                />
                <label htmlFor="published" style={{ fontWeight: 600, cursor: 'pointer' }}>Publish Article Immediately</label>
              </div>

              {/* Cover Image */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', gridColumn: 'span 2' }}>
                <label style={{ fontWeight: 600 }}>Cover Image</label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '250px', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>Upload Image file:</span>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleImageUpload} 
                      disabled={uploadingImage}
                      style={{ padding: '0.45rem', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)', background: 'var(--color-white)', cursor: 'pointer' }}
                    />
                    {uploadingImage && <small style={{ color: 'var(--color-primary)' }}>Uploading image...</small>}
                  </div>
                  <div style={{ flex: 1, minWidth: '250px', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>Or Paste URL:</span>
                    <input 
                      type="text" 
                      value={imageUrl} 
                      onChange={(e) => setImageUrl(e.target.value)} 
                      placeholder="https://example.com/cover-image.jpg"
                      style={{ padding: '0.7rem', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)', background: 'var(--color-white)' }}
                    />
                  </div>
                </div>
                {imageUrl && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>Current Cover Preview:</span>
                    <img src={getFullImageUrl(imageUrl)} alt="Preview" style={{ height: '50px', width: '80px', objectFit: 'cover', borderRadius: '4px', border: '1px solid rgba(0,0,0,0.1)' }} />
                  </div>
                )}
              </div>

            </div>

            {/* Content Textarea with Markdown Toolbar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <label style={{ fontWeight: 600 }}>Content * (Markdown format supported)</label>
              
              {/* Toolbar */}
              <div className="markdown-toolbar" style={{ 
                display: 'flex', 
                gap: '0.3rem', 
                background: 'var(--color-light, #f4f6f9)', 
                padding: '0.6rem', 
                borderTopLeftRadius: '8px', 
                borderTopRightRadius: '8px',
                border: '1px solid rgba(0,0,0,0.1)',
                borderBottom: 'none',
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                <button type="button" className="toolbar-btn" onClick={() => document.execCommand('undo')} title="Undo (Ctrl+Z)"><FiRotateCcw /></button>
                <button type="button" className="toolbar-btn" onClick={() => document.execCommand('redo')} title="Redo (Ctrl+Y)"><FiRotateCw /></button>
                <div style={{ width: '1px', height: '1.2rem', background: 'rgba(0,0,0,0.15)', margin: '0 0.2rem' }}></div>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('bold')} title="Bold"><FiBold /></button>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('italic')} title="Italic"><FiItalic /></button>
                <div style={{ width: '1px', height: '1.2rem', background: 'rgba(0,0,0,0.15)', margin: '0 0.2rem' }}></div>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('h1')} title="Heading 1"><span style={{fontWeight: 700, fontSize: '0.75rem'}}>H1</span></button>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('h2')} title="Heading 2"><span style={{fontWeight: 700, fontSize: '0.75rem'}}>H2</span></button>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('h3')} title="Heading 3"><span style={{fontWeight: 700, fontSize: '0.75rem'}}>H3</span></button>
                <div style={{ width: '1px', height: '1.2rem', background: 'rgba(0,0,0,0.15)', margin: '0 0.2rem' }}></div>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('link')} title="Insert Link"><FiLink /></button>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('list')} title="Bullet List"><FiList /></button>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('code')} title="Code Block"><FiCode /></button>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('quote')} title="Quote" style={{ fontFamily: 'serif', fontWeight: 'bold', fontSize: '1.1rem', lineHeight: '1' }}>"</button>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('newline')} title="Add Newline"><FiCornerDownLeft /></button>
                <div style={{ width: '1px', height: '1.2rem', background: 'rgba(0,0,0,0.15)', margin: '0 0.2rem' }}></div>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('align-left')} title="Align Left"><FiAlignLeft /></button>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('align-center')} title="Align Center"><FiAlignCenter /></button>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('align-right')} title="Align Right"><FiAlignRight /></button>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('align-justify')} title="Justify Content"><FiAlignJustify /></button>
                <div style={{ width: '1px', height: '1.2rem', background: 'rgba(0,0,0,0.15)', margin: '0 0.2rem' }}></div>
                <button type="button" className="toolbar-btn" onClick={() => document.getElementById('toolbar-image-uploader').click()} title="Upload Image & Insert"><FiUpload /></button>
                <button type="button" className="toolbar-btn" onClick={() => insertMarkdown('image')} title="Insert Image Link"><FiImage /></button>
                <input 
                  type="file" 
                  id="toolbar-image-uploader" 
                  accept="image/*" 
                  onChange={handleToolbarImageUpload} 
                  style={{ display: 'none' }} 
                />
              </div>

              <textarea 
                id="blog-content-textarea"
                value={content} 
                onChange={(e) => setContent(e.target.value)} 
                placeholder="Write your article body here... Highlight text and click buttons to format. Use double-newlines to separate paragraphs."
                required
                style={{ 
                  padding: '1rem', 
                  borderBottomLeftRadius: '6px', 
                  borderBottomRightRadius: '6px', 
                  borderTopLeftRadius: '0',
                  borderTopRightRadius: '0',
                  border: '1px solid rgba(0,0,0,0.1)', 
                  background: 'var(--color-white)', 
                  minHeight: '350px', 
                  fontFamily: 'inherit', 
                  resize: 'vertical', 
                  lineHeight: '1.6' 
                }}
              />
            </div>

            <button 
              type="submit" 
              className="btn primary" 
              disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', width: 'fit-content' }}
            >
              <FiSave /> {loading ? 'Saving...' : 'Save Article'}
            </button>
          </form>
        )}

        {/* LIVE PREVIEW VIEW */}
        {!fetching && activeTab === 'preview' && (
          <div className="blog__detail-page" style={{ padding: '2rem', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px', background: 'var(--color-white)', marginTop: '1.5rem' }}>
            <header className="blog__detail-header">
              <span className="blog__detail-cat-tag">{category}</span>
              <h1 className="blog__detail-title" style={{ fontSize: '2.5rem' }}>{title || 'Untitled Article'}</h1>
              {subtitle && <p className="blog__detail-subtitle" style={{ fontSize: '1.1rem' }}>{subtitle}</p>}
              
              <div className="blog__detail-meta-row" style={{ padding: '0.8rem 0' }}>
                <span style={{ fontWeight: 600 }}>By {authorName}</span>
                <span>{readTime || 'Auto Calculated'} • Preview Mode</span>
              </div>
            </header>

            {imageUrl && (
              <div className="blog__detail-image-wrapper" style={{ marginBottom: '2rem' }}>
                <img src={getFullImageUrl(imageUrl)} alt={title} style={{ width: '100%', height: 'auto', display: 'block' }} />
              </div>
            )}

            <div className="blog__article-content" style={{ fontSize: '1rem' }}>
              {renderPreviewContent(content)}
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default AdminBlogEdit;
